/**
 * Help Modal Component
 * Displays keyboard shortcuts and app information
 */

import { useTranslation } from 'react-i18next';
import { InstallButton } from './InstallButton';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="help-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
    >
      <div
        className="help-modal-content"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
      >
        <div className="help-modal-header">
          <h1 id="help-title" className="help-modal-title">
            {t('helpModal.title')}
          </h1>
          <button
            type="button"
            className="help-modal-close"
            onClick={onClose}
            aria-label={t('helpModal.close')}
          >
            ×
          </button>
        </div>

        <div className="help-modal-body">
          <div className="shortcut-section">
            <h2 className="section-title">{t('helpModal.sections.recording.title')}</h2>
            <div className="shortcut-list">
              <div className="shortcut-item">
                <kbd className="key">Space</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.recording.shortcuts.space')}
                </span>
              </div>
            </div>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">{t('helpModal.sections.editing.title')}</h2>
            <div className="shortcut-list">
              <div className="shortcut-item">
                <kbd className="key">Ctrl</kbd> + <kbd className="key">Enter</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.editing.shortcuts.ctrlEnter')}
                </span>
              </div>
              <div className="shortcut-item">
                <kbd className="key">Ctrl</kbd> + <kbd className="key">K</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.editing.shortcuts.ctrlK')}
                </span>
              </div>
              <div className="shortcut-item">
                <kbd className="key">Ctrl</kbd> + <kbd className="key">C</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.editing.shortcuts.ctrlC')}
                </span>
              </div>
              <div className="shortcut-item">
                <kbd className="key">Ctrl</kbd> + <kbd className="key">Z</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.editing.shortcuts.ctrlZ')}
                </span>
              </div>
              <div className="shortcut-item">
                <kbd className="key">Escape</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.editing.shortcuts.escape')}
                </span>
              </div>
            </div>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">{t('helpModal.sections.help.title')}</h2>
            <div className="shortcut-list">
              <div className="shortcut-item">
                <kbd className="key">F1</kbd> or <kbd className="key">h</kbd> or{' '}
                <kbd className="key">?</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.help.shortcuts.f1')}
                </span>
              </div>
              <div className="shortcut-item">
                <kbd className="key">Escape</kbd>
                <span className="shortcut-description">
                  {t('helpModal.sections.help.shortcuts.escape')}
                </span>
              </div>
            </div>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">Gesture Controls (Mobile)</h2>
            <div className="shortcut-list">
              <div className="shortcut-item">
                <span className="gesture-icon">👆</span>
                <span className="shortcut-description">
                  <strong>Swipe up on text area:</strong> Clear all content
                </span>
              </div>
              <div className="shortcut-item">
                <span className="gesture-icon">👆</span>
                <span className="shortcut-description">
                  <strong>Swipe down on text area:</strong> Clear refined text only
                </span>
              </div>
              <div className="shortcut-item">
                <span className="gesture-icon">👆</span>
                <span className="shortcut-description">
                  <strong>Swipe left/right on text area:</strong> Undo last action
                </span>
              </div>
              <div className="shortcut-item">
                <span className="gesture-icon">🤏</span>
                <span className="shortcut-description">
                  <strong>Long press record button:</strong> Toggle recording lock
                </span>
              </div>
            </div>
            <div className="gesture-note">
              <p>
                <strong>Note:</strong> Gestures require touch-enabled devices. Swipe gestures have
                configurable thresholds to prevent accidental activation.
              </p>
            </div>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">{t('helpModal.sections.features.title')}</h2>
            <ul className="feature-list">
              <li>• Auto-stop recording after 3 seconds of silence</li>
              <li>• Local transcription when offline</li>
              <li>• Real-time audio waveform visualization</li>
              <li>• Word-level alternatives and confidence scoring</li>
              <li>• Click words to see alternatives and refine them</li>
              <li>• Undo deleted content within 10 seconds</li>
              <li>• Mobile-responsive design</li>
              <li>• Dark mode support</li>
              <li>• Progressive Web App (PWA) - installable for offline use</li>
            </ul>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">Install App</h2>
            <div className="install-section">
              <p>Install Refinevoice on your device for the best experience:</p>
              <div className="install-button-container">
                <InstallButton />
              </div>
              <div className="install-note">
                <p>
                  <strong>Desktop:</strong> Click the install button or look for the install icon in
                  your browser's address bar.
                </p>
                <p>
                  <strong>Mobile (Android):</strong> Use the install button or follow your browser's
                  "Add to Home screen" prompt.
                </p>
                <p>
                  <strong>Mobile (iOS):</strong> In Safari, tap Share → "Add to Home Screen".
                </p>
              </div>
            </div>
          </div>

          <div className="shortcut-section">
            <h2 className="section-title">{t('helpModal.sections.wordAlternatives.title')}</h2>
            <ul className="feature-list">
              <li>
                • <strong>Red underline:</strong> Low confidence (&lt;60%)
              </li>
              <li>
                • <strong>Yellow underline:</strong> Medium confidence (60-80%)
              </li>
              <li>
                • <strong>Click any word:</strong> See alternatives and actions
              </li>
              <li>
                • <strong>Refine word:</strong> AI-powered word improvement
              </li>
              <li>
                • <strong>Delete word:</strong> Remove unwanted words
              </li>
              <li>
                • <strong>Rerecord:</strong> Replace with new recording
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
